import React from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Input,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { Draggable } from "react-beautiful-dnd";
import { DeleteIcon } from "@chakra-ui/icons";
import { MdDragIndicator } from "react-icons/md";

const DraggableChoice = ({
  choice,
  index,
  onChange,
  onDelete,
  showCorrectAnswer = false,
  onToggleCorrect,
  disableDelete = false,
}) => {
  return (
    <Draggable draggableId={choice.id} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          bg="white"
          border="1px solid"
          borderColor="#D0CCD5"
          borderRadius="4px"
          p={3}
          position="relative"
          opacity={snapshot.isDragging ? 0.8 : 1}
          transform={snapshot.isDragging ? "rotate(2deg)" : "none"}
          transition="all 0.2s"
        >
          {/* Main content area */}
          <VStack spacing={2} align="stretch">
            <HStack spacing={2} align="center">
              {/* Drag Handle */}
              <Box
                {...provided.dragHandleProps}
                w="18px"
                h="18px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="grab"
                _active={{ cursor: "grabbing" }}
              >
                <MdDragIndicator
                  w="6px"
                  h="9px"
                  color="#141B34"
                  opacity={0.6}
                />
              </Box>

              {/* Choice Input */}
              <Input
                value={choice.option}
                onChange={(e) => onChange(choice.id, e.target.value)}
                fontSize="12px"
                fontWeight="84"
                lineHeight="1.5"
                letterSpacing="-0.01em"
                color="#0C001C"
                fontFamily="Geist, sans-serif"
                border="none"
                p={0}
                bg="transparent"
                _focus={{
                  boxShadow: "none",
                  borderColor: "transparent",
                }}
                placeholder="Enter choice text"
                data-testid={`quiz-answer-input-${index}`}
              />

              {/* Vertical divider */}
              <Box
                w="0px"
                h="39px"
                borderLeft="1px solid"
                borderColor="#FFFFFF"
              />

              {/* Delete Button */}
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                color={disableDelete ? "#B8B2C0" : "#433358"}
                w="18px"
                h="18px"
                minW="18px"
                onClick={() => !disableDelete && onDelete(choice.id)}
                cursor={disableDelete ? "not-allowed" : "pointer"}
                _hover={
                  disableDelete
                    ? {}
                    : {
                        bg: "rgba(67, 51, 88, 0.1)",
                        color: "#433358",
                      }
                }
                aria-label="Delete choice"
                isDisabled={disableDelete}
              />
            </HStack>

            {/* Correct Answer Selector (for quiz mode) - now below the main content */}
            {showCorrectAnswer && (
              <Box
                cursor="pointer"
                onClick={() => onToggleCorrect?.(choice.id)}
                ml={6} // Align with the input content
                data-testid={`quiz-correct-checkbox-${index}`}
              >
                <Flex
                  align="center"
                  gap="5px"
                  bg="white"
                  px={2}
                  py={1}
                  borderRadius="4px"
                  boxShadow="sm"
                  fontSize="12px"
                  fontWeight="84"
                  color="#726682"
                  fontFamily="Geist, sans-serif"
                  _hover={{ bg: "gray.50" }}
                  width="fit-content"
                >
                  <Box
                    w="18px"
                    h="18px"
                    borderRadius="50%"
                    border="1px solid #D0CCD5"
                    bg={choice.correct ? "green.500" : "white"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {choice.correct && (
                      <Text color="white" fontSize="10px">
                        ✓
                      </Text>
                    )}
                  </Box>
                  <Text>Correct answer</Text>
                </Flex>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </Draggable>
  );
};

export default DraggableChoice;
